import { Link } from 'react-router';
import { ButtonLink } from '@/components/ui/Button';
import { Pill, PillGroup } from '@/components/ui/Pill';
import type { CourseSummary, RoundStatus, RoundSummary } from '@/lib/api/types';
import { cx } from '@/lib/cx';
import { fmtDateShort, fmtStartedAt } from '@/lib/format/date';
import { fmtDecimal, fmtInt, fmtToPar } from '@/lib/format/number';
import { courseNameFrom } from '@/lib/api/queries';
import { t } from '@/lib/i18n';
import { routes } from '@/routes/paths';
import {
  DEFAULT_FILTERS,
  PERIOD_LABEL,
  STATUS_LABEL,
  isDefaultFilters,
  roundDate,
  type HolesKey,
  type PeriodKey,
  type RoundFilters,
} from './filters';
import styles from './RoundsList.module.css';

/**
 * A lista de voltas no desktop (canvas 1v).
 *
 * A diferença não é de tamanho, é de gênero: o celular empilha cartões porque não tem largura,
 * e aqui os números ganham colunas alinhadas à direita, comparáveis de cima a baixo. É o que
 * torna a lista um instrumento em vez de um histórico.
 */

const STATUSES: Array<RoundStatus | 'all'> = ['all', 'completed', 'in_progress', 'abandoned'];
const HOLE_KEYS: HolesKey[] = ['both', 18, 9];

/**
 * Os filtros que no celular vivem num bottom sheet ficam permanentes: no desktop o custo de
 * mostrá-los é zero e o de esconder é um clique a mais por recorte.
 */
export function RoundsFilterBar({
  filters,
  onChange,
  courses,
}: {
  filters: RoundFilters;
  onChange: (next: RoundFilters) => void;
  courses: CourseSummary[];
}) {
  return (
    <div className={styles.filterBar}>
      <div className={styles.filterGroup}>
        <span className={styles.filterLabel}>{t('rounds.filter.statusLabel')}</span>
        <PillGroup label={t('rounds.filter.statusAria')}>
          {STATUSES.map((s) => (
            <Pill key={s} selected={filters.status === s} onClick={() => onChange({ ...filters, status: s })}>
              {t(STATUS_LABEL[s])}
            </Pill>
          ))}
        </PillGroup>
      </div>

      <div className={styles.filterGroup}>
        <span className={styles.filterLabel}>{t('rounds.filter.holesLabel')}</span>
        <PillGroup label={t('rounds.filter.holesAria')}>
          {HOLE_KEYS.map((h) => (
            <Pill key={String(h)} selected={filters.holes === h} onClick={() => onChange({ ...filters, holes: h })}>
              {h === 'both' ? t('rounds.filter.bothHoles') : String(h)}
            </Pill>
          ))}
        </PillGroup>
      </div>

      <select
        className={styles.filterSelect}
        value={filters.period}
        aria-label={t('rounds.filter.periodAria')}
        onChange={(e) => onChange({ ...filters, period: e.target.value as PeriodKey })}
      >
        {(Object.keys(PERIOD_LABEL) as PeriodKey[]).map((p) => (
          <option key={p} value={p}>
            {p === 'all' ? t('rounds.filter.allPeriods') : t(PERIOD_LABEL[p])}
          </option>
        ))}
      </select>

      <select
        className={styles.filterSelect}
        value={filters.courseId}
        aria-label={t('rounds.filter.courseAria')}
        onChange={(e) => onChange({ ...filters, courseId: e.target.value })}
      >
        <option value="all">{t('rounds.filter.allCourses')}</option>
        {courses.map((c) => (
          <option key={c.id} value={c.id}>
            {c.name}
          </option>
        ))}
      </select>

      {/* "Limpar" só aparece quando há o que limpar — senão é um botão que não faz nada. */}
      {!isDefaultFilters(filters) && (
        <button type="button" className={styles.filterClear} onClick={() => onChange(DEFAULT_FILTERS)}>
          {t('rounds.filter.clear')}
        </button>
      )}
    </div>
  );
}

/**
 * As voltas em andamento, fixadas acima do cabeçalho da tabela. Elas não têm score para
 * comparar, e misturá-las quebraria a coluna — então ficam fora dela, com a ação que lhes cabe.
 *
 * O comportamento continua valendo; o parágrafo que o explicava saiu da tela (canvas 1v). A
 * posição já diz o que ele dizia: quem está acima do cabeçalho está fora da ordem.
 */
export function ActiveRoundsStrip({
  rounds,
  courseIndex,
  scoredById,
}: {
  rounds: RoundSummary[];
  courseIndex: Map<string, CourseSummary> | undefined;
  scoredById: Map<string, number>;
}) {
  if (rounds.length === 0) return null;

  return (
    <div className={styles.activeStrip}>
      {rounds.map((r) => (
        <div key={r.id} className={styles.activeCard}>
          <div className={styles.activeBody}>
            <div className={styles.activeCourse}>{courseNameFrom(courseIndex, r.courseId)}</div>
            <div className={styles.activeMeta}>
              {t('rounds.active.meta', {
                started: fmtStartedAt(r.startedAt),
                holes: r.holesPlayed,
                tee: r.teeBoxName,
              })}
            </div>
          </div>
          <div className={styles.activeProgress}>
            {t('rounds.active.progress', {
              scored: scoredById.get(r.id) ?? 0,
              holes: r.holesPlayed,
            })}
          </div>
          <ButtonLink to={routes.round(r.id)} size="sm" variant="secondary">
            {t('rounds.continue')}
          </ButtonLink>
        </div>
      ))}
    </div>
  );
}

export function RoundsTable({
  rounds,
  courseIndex,
  bestRoundId,
}: {
  rounds: RoundSummary[];
  courseIndex: Map<string, CourseSummary> | undefined;
  bestRoundId: string | null;
}) {
  return (
    <table className={styles.table}>
      <thead>
        <tr>
          <th scope="col">{t('rounds.table.course')}</th>
          <th scope="col" className={styles.num}>
            {t('rounds.table.strokes')}
          </th>
          <th scope="col" className={styles.num}>
            {t('rounds.table.par')}
          </th>
          <th scope="col" className={styles.num}>
            {t('rounds.table.sd')}
          </th>
          <th scope="col">
            <span className="visually-hidden">{t('rounds.table.open')}</span>
          </th>
        </tr>
      </thead>
      <tbody>
        {rounds.map((r) => (
          <tr key={r.id}>
            <td>
              <div className={styles.cellCourse}>{courseNameFrom(courseIndex, r.courseId)}</div>
              <div className={styles.cellMeta}>
                {t('rounds.rowMeta', {
                  date: fmtDateShort(roundDate(r)),
                  holes: r.holesPlayed,
                  tee: r.teeBoxName,
                })}
                {r.status === 'abandoned' && t('rounds.rowAbandoned')}
              </div>
              {(r.groupPlayerCount != null || r.id === bestRoundId) && (
                <span className={styles.cellBadges}>
                  {r.groupPlayerCount != null && (
                    <span className={styles.groupBadge}>
                      {t('rounds.groupBadge', { count: r.groupPlayerCount })}
                    </span>
                  )}
                  {r.id === bestRoundId && <span className={styles.bestBadge}>{t('rounds.best')}</span>}
                </span>
              )}
            </td>
            <td className={styles.num}>
              <span className={cx(styles.cellStrokes, r.totalStrokes == null && styles.cellEmpty)}>
                {fmtInt(r.totalStrokes)}
              </span>
              {r.totalToPar != null && (
                <span className={styles.cellToPar}>{fmtToPar(r.totalToPar)}</span>
              )}
            </td>
            <td className={cx(styles.num, styles.cellQuiet)}>{fmtInt(parOf(r))}</td>
            <td className={styles.num}>{fmtDecimal(r.scoreDifferential)}</td>
            <td className={styles.openCell}>
              <Link to={routes.round(r.id)} className={styles.openLink}>
                {t('rounds.table.open')}
              </Link>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/**
 * O par da volta não vem do servidor, mas sai exato das duas colunas que vêm: se o jogador fez
 * 84 com +12, o par era 72. Derivar é melhor que buscar o par do campo, que pode ter mudado
 * depois — e que, num tee combinado, nem seria o par que essa volta jogou.
 */
function parOf(r: RoundSummary): number | null {
  if (r.totalStrokes == null || r.totalToPar == null) return null;
  return r.totalStrokes - r.totalToPar;
}
